import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isValidEmail, isValidIndianPlate, formatIndianPlate } from "../lib/validation";
import { StatusPill } from "../components/StatusPill";
import { Timeline } from "../components/Timeline";
import { Wrench, AlertTriangle, IndianRupee, ExternalLink, User, Phone, Search, MessageCircle, ArrowLeft, Car, RotateCcw, MapPin, Star, Mail, KeyRound, LogOut } from "lucide-react";
import MacJitLogo from "../components/MacJitLogo";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";

/**
 * Secure customer tracker.
 * Customer verifies their registered email with OTP and sees:
 *   - active booking + live status & timeline
 *   - bill (if generated) + Pay Now
 *   - "Send bill by email" button
 *   - approval prompt (if mechanic raised one)
 */
export default function CustomerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const { user, loading, requestTrackerOtp, verifyTrackerOtp, logout } = useAuth();
  const initial = (searchParams.get("plate") || "").toUpperCase();

  const [plate, setPlate] = useState(initial);
  const [data, setData] = useState(null);   // { active, history, invoice_url }
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [ratingBusy, setRatingBusy] = useState(false);

  const lookup = async (p = plate) => {
    const q = (p || "").trim().toUpperCase();
    if (q && !isValidIndianPlate(q)) return toast.error("Enter a valid Indian car number, e.g. KA-05-MN-2024");
    setBusy(true); setSearched(true);
    try {
      const r = await api.get("/track/bookings", { params: q ? { plate: q } : {} });
      const history = r.data?.history || [];
      const active = r.data?.active || null;
      if (!active && q) toast.error("No booking found for that car in your account");
      setData({
        active,
        history,
        invoice_url: active ? `/api/invoices/${active.id}.pdf` : "",
      });
      if (q) setSearchParams({ plate: q });
      else setSearchParams({});
    } catch (err) {
      setData(null);
      toast.error(err.response?.data?.detail || "Could not load your bookings");
    } finally { setBusy(false); }
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!isValidEmail(email)) return toast.error("Enter a valid email address");
    setBusy(true);
    try {
      const r = await requestTrackerOtp(email.trim().toLowerCase());
      setOtpSent(true);
      toast.success(r.masked_email ? `OTP sent to ${r.masked_email}` : "If this account exists, an OTP has been sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !otp.trim()) return toast.error("Enter email and OTP");
    setBusy(true);
    try {
      await verifyTrackerOtp(email.trim().toLowerCase(), otp.trim());
      toast.success("Email verified");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired OTP");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!loading && ["customer_tracker", "customer"].includes(user?.role)) lookup(initial || "");
    // eslint-disable-next-line
  }, [loading, user?.id]);

  // Refresh every 15s while a booking is being viewed and not yet PAID.
  useEffect(() => {
    if (!user || !data?.active || data.active.status === "PAID") return;
    const t = setInterval(() => lookup(plate), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data?.active?.id]);

  const active = data?.active;

  if (loading) {
    return <div className="min-h-screen bg-zinc-100 grid place-items-center font-mono text-sm text-zinc-500">Loading secure tracker...</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-100 text-zinc-900">
        <header className="bg-white/80 backdrop-blur-xl border-b border-zinc-200">
          <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
            <button onClick={() => nav("/")} className="flex items-center gap-2 group">
              <MacJitLogo size={32} />
              <span className="font-display font-black text-lg tracking-tighter group-hover:text-orange-500">MACJIT</span>
            </button>
            <button onClick={() => nav("/")} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500">
              <ArrowLeft className="w-3 h-3" /> Home
            </button>
          </div>
        </header>
        <main className="max-w-md mx-auto px-5 py-12">
          <div className="bg-white border border-zinc-200 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Secure car tracker</p>
                <h1 className="font-display font-black text-3xl tracking-tight mt-1">Verify Email</h1>
              </div>
              <div className="w-12 h-12 bg-orange-500 text-black grid place-items-center">
                <KeyRound className="w-6 h-6" />
              </div>
            </div>
            <form onSubmit={otpSent ? submitOtp : requestOtp} className="space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Registered email</label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input data-testid="track-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} required className="w-full bg-zinc-50 border border-zinc-200 pl-10 pr-4 py-3 font-mono focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              {otpSent && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email OTP</label>
                  <div className="relative mt-2">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input data-testid="track-otp" inputMode="numeric" maxLength={6} placeholder="6 digit OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required className="w-full bg-zinc-50 border border-zinc-200 pl-10 pr-4 py-3 font-mono focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
              )}
              <button data-testid="track-auth-submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-4 transition-colors disabled:opacity-50">
                {busy ? "Please wait..." : otpSent ? "Verify OTP" : "Send OTP"}
              </button>
              {otpSent && (
                <button type="button" onClick={requestOtp} disabled={busy} className="w-full border border-zinc-300 text-zinc-500 hover:text-orange-500 font-mono text-[10px] uppercase tracking-widest py-3">
                  Resend OTP
                </button>
              )}
            </form>
            <p className="mt-5 font-mono text-xs text-zinc-500">Use the same email shared during enquiry, booking, or billing.</p>
          </div>
        </main>
      </div>
    );
  }

  if (!["customer_tracker", "customer"].includes(user.role)) {
    return (
      <div className="min-h-screen bg-zinc-100 grid place-items-center px-5">
        <div className="max-w-md bg-white border border-zinc-200 p-6 text-center">
          <h1 className="font-display font-black text-2xl tracking-tight">Customer tracker only</h1>
          <p className="mt-2 text-sm text-zinc-500">You are signed in as {user.role}. Staff should use their dashboard.</p>
          <button onClick={() => nav(`/${user.role}`)} className="mt-5 bg-black text-orange-500 font-display font-black uppercase tracking-widest px-5 py-3">Go to dashboard</button>
        </div>
      </div>
    );
  }

  const carLocation = (booking) => {
    if (!booking) return null;
    if (booking.returned_from_qa || booking.qa_fail_reasons?.length > 0 || booking.qa_fail_reason) {
      return {
        title: "Back with mechanic for QA rework",
        place: booking.bay_name || "Service bay",
        owner: booking.mechanic_name || "Assigned mechanic",
        detail: "QA rejected this job and sent it back with the reason shown below.",
        tone: "red",
      };
    }
    const map = {
      BOOKED: ["Reception queue", "Front desk", "Your car is booked and waiting for assignment."],
      ASSIGNED: ["Assigned service bay", booking.bay_name || "Service bay", `Assigned to ${booking.mechanic_name || "mechanic"}.`],
      IN_SERVICE: ["Under service", booking.bay_name || "Mechanic bay", `${booking.mechanic_name || "Mechanic"} is working on it now.`],
      READY_TO_TEST: ["QA testing area", "QA bay", `${booking.tester_name || "QA tester"} will inspect the car.`],
      READY_FOR_QA: ["QA testing area", "QA bay", `${booking.tester_name || "QA tester"} will inspect the car.`],
      QA_DONE: ["Billing counter", "Reception", "QA passed. Bill can be generated now."],
      READY_FOR_BILL: ["Billing counter", "Reception", "QA passed. Bill can be generated now."],
      BILLED: ["Ready for payment", "Reception", "Invoice is ready. Please complete payment."],
      PAID: ["Ready for handover", "Delivery area", "Payment received. Please collect your car."],
    };
    const row = map[booking.status] || ["Garage floor", booking.bay_name || "MacJit Garage", "We are updating the latest location."];
    return { title: row[0], place: row[1], owner: booking.mechanic_name || booking.tester_name || "MacJit team", detail: row[2], tone: "dark" };
  };

  const location = carLocation(active);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${active.id}/approve`, { plate_number: active.plate_number });
      toast.success("Approved — your mechanic can continue.");
      lookup(plate);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    } finally { setBusy(false); }
  };

  const sendEmailBill = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/track/${active.id}/send-bill`, { plate_number: active.plate_number });
      toast.success(`Bill sent by email to ${r.data.sent_to || "registered email"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send bill");
    } finally { setBusy(false); }
  };

  const reopen = async (booking) => {
    setBusy(true);
    try {
      const r = await api.post(`/track/${booking.id}/reopen`, {
        plate_number: booking.plate_number,
        customer_phone: booking.customer_phone,
        problem: "Follow-up visit within 7 days",
      });
      toast.success("Follow-up booking created");
      setPlate(r.data.booking.plate_number);
      lookup(r.data.booking.plate_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reopen this service");
    } finally { setBusy(false); }
  };

  const canReopen = (booking) => {
    if (!booking || !["PAID", "QA_DONE", "BILLED"].includes(booking.status)) return false;
    const raw = booking.paid_at || booking.qa_done_at || booking.finished_at || booking.created_at;
    if (!raw) return false;
    return Date.now() - new Date(raw).getTime() <= 7 * 24 * 60 * 60 * 1000;
  };

  const submitRating = async () => {
    if (!active) return;
    setRatingBusy(true);
    try {
      await api.post("/public/ratings", {
        booking_id: active.id,
        plate_number: active.plate_number,
        rating,
        comment: review,
      });
      toast.success("Thanks for rating your mechanic");
      setReview("");
      lookup(plate);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit rating");
    } finally {
      setRatingBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900">
      <div className="bg-black text-orange-500 py-1.5">
        <Marquee speed={40} gradient={false}>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] mx-8">SECURE EMAIL OTP · TRACK YOUR CAR · EMAIL BILL · LIVE STATUS · YOUR CAR OUR PRIORITY ·</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] mx-8">SECURE EMAIL OTP · TRACK YOUR CAR · EMAIL BILL · LIVE STATUS · YOUR CAR OUR PRIORITY ·</span>
        </Marquee>
      </div>

      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <button onClick={() => nav("/")} className="flex items-center gap-2 group">
            <MacJitLogo size={32} />
            <span className="font-display font-black text-lg tracking-tighter group-hover:text-orange-500">MACJIT</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/")} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500">
              <ArrowLeft className="w-3 h-3" /> Home
            </button>
            <button onClick={logout} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-8 pb-20">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Track your service</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Car Tracker</h1>
          <p className="font-mono text-xs text-zinc-500 mt-2">Your verified email unlocks your bookings. Search by plate if you have multiple cars.</p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); lookup(); }}
          className="bg-white border border-zinc-200 p-5 flex flex-col sm:flex-row gap-3 shadow-sm"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              data-testid="plate-input"
              autoFocus
              placeholder="Optional plate filter, e.g. KA-05-MN-2024"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && !isValidIndianPlate(value)) toast.error("Enter a valid Indian car number, e.g. KA-05-MN-2024");
                if (value && isValidIndianPlate(value)) setPlate(formatIndianPlate(value));
              }}
              className="w-full bg-zinc-50 border border-zinc-200 pl-10 pr-4 py-3 font-mono text-base focus:border-orange-500 focus:outline-none uppercase tracking-wide"
            />
          </div>
          <button
            data-testid="plate-search"
            disabled={busy}
            className="bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest px-6 py-3 transition-colors disabled:opacity-50"
          >
            {busy ? "Looking..." : "Show Cars →"}
          </button>
        </form>

        {searched && !active && !busy && (
          <div data-testid="no-booking" className="border border-dashed border-zinc-300 p-12 text-center bg-white">
            <Wrench className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <p className="font-display font-black text-xl uppercase">No booking found</p>
            <p className="text-sm text-zinc-500 mt-1">No booking is linked to this verified email and plate filter.</p>
          </div>
        )}

        {active && (
          <>
            <section className="bg-white border border-zinc-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="active-booking-card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Booking for</p>
                  <h2 className="font-display font-black text-2xl mt-0.5">{active.car_make} {active.car_model}</h2>
                  <p className="font-mono text-sm text-zinc-600 mt-0.5">{active.plate_number} · {active.service_type}</p>
                  <p className="font-mono text-xs text-zinc-500 mt-1">Customer: {active.customer_name}</p>
                </div>
                <StatusPill status={active.status} testid="customer-status-pill" />
              </div>

              {active.garage_presence_status === "NOT_IN_GARAGE" && active.status === "BOOKED" && (
                <div data-testid="not-in-garage-banner" className="mt-4 border border-blue-200 bg-blue-50 p-4 flex gap-3">
                  <Car className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-black uppercase text-blue-950">Booked, not in garage yet</p>
                    <p className="font-mono text-xs text-blue-800 mt-1">
                      Drop your car on or before {active.drop_deadline_at ? new Date(active.drop_deadline_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "your slot time"}.
                      If it is not dropped by then, this booking auto-cancels.
                    </p>
                  </div>
                </div>
              )}

              {(active.mechanic_name || active.bay_name) && (
                <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-2 gap-4 font-mono text-sm">
                  <div data-testid="assigned-mechanic">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><User className="w-3 h-3"/>Mechanic</p>
                    <p className="font-bold">{active.mechanic_name || "—"}</p>
                    {active.mechanic_phone && (
                      <a href={`tel:${active.mechanic_phone}`} className="text-[11px] text-orange-600 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3"/>{active.mechanic_phone}
                      </a>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Service Bay</p>
                    <p className="font-bold">{active.bay_name || "—"}</p>
                  </div>
                  {active.tester_name && (
                    <div data-testid="assigned-tester">
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><User className="w-3 h-3"/>QA Tester</p>
                      <p className="font-bold">{active.tester_name}</p>
                    </div>
                  )}
                </div>
              )}

              {active.estimated_start_at && active.status !== "PAID" && (
                <div data-testid="eta-banner" className="mt-3 bg-black text-white p-3 flex items-center justify-between">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Estimated slot</p>
                    <p className="font-display font-bold text-sm">
                      {new Date(active.estimated_start_at).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                      {" → "}
                      {active.estimated_end_at && new Date(active.estimated_end_at).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              )}
            </section>

            {location && (
              <section
                data-testid="customer-car-location"
                className={`border-2 p-5 ${
                  location.tone === "red"
                    ? "bg-red-50 border-red-500 text-red-950"
                    : "bg-white border-zinc-900 text-zinc-950"
                }`}
              >
                <div className="flex items-start gap-3">
                  <MapPin className={`w-6 h-6 flex-shrink-0 mt-0.5 ${location.tone === "red" ? "text-red-600" : "text-orange-500"}`} />
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">Car location now</p>
                    <h3 className="font-display font-black text-2xl tracking-tight mt-1">{location.title}</h3>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3 font-mono text-xs">
                      <div>
                        <p className="uppercase tracking-widest opacity-60">Place</p>
                        <p className="font-bold text-sm">{location.place}</p>
                      </div>
                      <div>
                        <p className="uppercase tracking-widest opacity-60">Responsible</p>
                        <p className="font-bold text-sm">{location.owner}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm">{location.detail}</p>
                    {active.qa_failed_at && (
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest opacity-70">
                        QA returned at {new Date(active.qa_failed_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {active.approval_pending && (
              <section data-testid="approval-card" className="bg-yellow-50 border-2 border-orange-500 p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-display font-black text-lg uppercase">Approval needed</h3>
                    <p className="text-sm mt-1">{active.approval_reason}</p>
                    <p className="font-mono text-sm font-bold mt-2">Extra cost: ₹{active.extra_cost}</p>
                    <button
                      data-testid="approve-work-btn"
                      onClick={approve}
                      disabled={busy}
                      className="mt-4 bg-black hover:bg-zinc-800 text-white font-display font-black uppercase tracking-widest px-6 py-3 transition-colors disabled:opacity-50"
                    >Approve →</button>
                  </div>
                </div>
              </section>
            )}

            {(active.returned_from_qa || active.qa_fail_reasons?.length > 0 || active.qa_fail_reason) && (
              <section data-testid="customer-qa-rework-card" className="bg-red-50 border-2 border-red-500 p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-display font-black text-lg uppercase text-red-950">QA requested rework</h3>
                    <p className="text-sm mt-1 text-red-900">
                      Your car has been sent back to {active.mechanic_name || "the mechanic"} for the same job. We will update this tracker once the rework is completed.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(active.qa_fail_reasons?.length ? active.qa_fail_reasons : [active.qa_fail_reason]).filter(Boolean).map((reason) => (
                        <span key={reason} className="border border-red-300 bg-white px-2 py-1 font-mono text-xs text-red-700">
                          {reason}
                        </span>
                      ))}
                    </div>
                    {active.qa_fail_notes && (
                      <p className="mt-3 text-sm text-red-800">
                        <span className="font-bold">QA note:</span> {active.qa_fail_notes}
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {["BILLED", "PAID"].includes(active.status) && (() => {
              const billItems = active.items || [];
              const itemsSum = billItems.reduce((s, i) => s + (i.subtotal || 0), 0);
              const billTotal = Number(active.bill_amount || 0);
              const sub = Number(active.subtotal || billTotal + Number(active.discount || 0));
              const base = Math.max(0, sub - itemsSum - Number(active.extra_cost || 0));
              const svc = String(active.service_type || "service").replace(/-/g, " ");
              const fallbackLines = [
                { key: "service", label: `${svc} - base`, amount: base },
                ...billItems.map((it) => ({ key: it.inventory_id || it.name, label: `${it.name} x ${it.qty}`, amount: it.subtotal })),
                ...(Number(active.extra_cost || 0) > 0 ? [{ key: "heavy", label: "Heavy work", amount: active.extra_cost }] : []),
              ].filter((line) => Number(line.amount || 0) > 0);
              const baseLines = active.base_lines?.length ? active.base_lines : fallbackLines;
              const billingAddons = active.billing_addons || [];
              return (
                <section data-testid="payment-card" className="bg-black text-white p-6">
                  <div className="mb-5 flex items-center gap-2">
                    <MacJitLogo size={28} />
                    <div>
                      <p className="font-display font-black tracking-tighter">MACJIT GARAGE</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Official service bill</p>
                    </div>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Service breakdown</p>
                  <div className="mt-3 divide-y divide-white/15 font-mono text-sm text-white" data-testid="customer-bill-breakdown">
                    {baseLines.map((line, idx) => (
                      <div key={line.key || `${line.label}-${idx}`} className="flex justify-between py-2 text-white">
                        <span>{line.label}</span>
                        <span className="font-bold">₹{line.amount}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 text-zinc-200">
                      <span>Subtotal</span>
                      <span className="font-bold">₹{sub}</span>
                    </div>
                    {Number(active.discount || 0) > 0 && (
                      <div className="flex justify-between py-2 text-emerald-300">
                        <span>{active.loyalty_tier || ""} loyalty ({active.discount_pct || 0}%)</span>
                        <span className="font-bold">−₹{active.discount}</span>
                      </div>
                    )}
                    {billingAddons.map((line, idx) => (
                      <div key={line.key || `${line.label}-${idx}`} className="flex justify-between py-2 text-orange-300">
                        <span>{line.label}</span>
                        <span className="font-bold">₹{line.amount}</span>
                      </div>
                    ))}
                  </div>

                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mt-5">{active.paid ? "Total paid" : "Total due"}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <IndianRupee className="w-7 h-7 text-orange-500" />
                    <span className="font-display font-black text-5xl tracking-tighter">{billTotal}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-5">
                    {!active.paid && (
                      <a
                        data-testid="pay-bill-btn"
                        href={`/pay/${active.id}?plate=${encodeURIComponent(active.plate_number || "")}`}
                        className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest px-6 py-3 transition-colors"
                      >Pay Now <ExternalLink className="w-4 h-4" /></a>
                    )}
                    <a
                      data-testid="download-invoice-billed"
                      href={`${BACKEND_URL || ""}/api/invoices/${active.id}.pdf`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 border border-white/30 hover:border-orange-500 hover:text-orange-500 text-white font-display font-black uppercase tracking-widest px-6 py-3 transition-colors"
                    >Download Invoice <ExternalLink className="w-4 h-4" /></a>
                    {!active.paid && (
                      <button
                        data-testid="send-whatsapp-bill"
                        onClick={sendEmailBill}
                        disabled={busy}
                        className="inline-flex items-center gap-2 border border-white/30 hover:border-orange-500 hover:text-orange-500 text-white font-display font-black uppercase tracking-widest px-6 py-3 transition-colors disabled:opacity-50"
                      ><MessageCircle className="w-4 h-4" /> Send bill by email</button>
                    )}
                  </div>
                </section>
              );
            })()}

            {active.paid && (
              <div data-testid="thanks-card" className="bg-emerald-500 text-white p-8 text-center">
                <div className="mb-4 flex items-center justify-center gap-2">
                  <MacJitLogo size={34} />
                  <span className="font-display font-black text-xl tracking-tighter">MACJIT GARAGE</span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em]">Thank you for visiting</p>
                <p className="font-display font-black text-3xl mt-1 tracking-tighter">Drive safe! 🏁</p>
                <a
                  data-testid="download-invoice-paid"
                  href={`${BACKEND_URL || ""}/api/invoices/${active.id}.pdf`}
                  target="_blank" rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 bg-black text-orange-500 font-display font-black uppercase tracking-widest px-5 py-3"
                >Download Invoice <ExternalLink className="w-3 h-3"/></a>
                {canReopen(active) && (
                  <button
                    onClick={() => reopen(active)}
                    disabled={busy}
                    className="mt-3 ml-2 inline-flex items-center gap-2 bg-white text-emerald-700 font-display font-black uppercase tracking-widest px-5 py-3 disabled:opacity-50"
                  >
                    <RotateCcw className="w-3 h-3" /> Reopen within 7 days
                  </button>
                )}
              </div>
            )}

            {active.paid && !active.customer_rating && (
              <section data-testid="mechanic-rating-card" className="bg-white border-2 border-orange-500 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Rate your mechanic</p>
                <h3 className="font-display font-black text-2xl tracking-tight mt-1">Help us reward good work</h3>
                <p className="text-sm text-zinc-500 mt-1">Your rating helps MacJit identify and gift mechanics who deliver great service.</p>
                <div className="flex gap-2 justify-center sm:justify-start mt-5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} className="p-1">
                      <Star className={`w-8 h-8 ${n <= rating ? "fill-orange-500 text-orange-500" : "text-zinc-300"}`} />
                    </button>
                  ))}
                </div>
                <textarea
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder={`Say something about ${active.mechanic_name || "your mechanic"}...`}
                  rows={3}
                  className="mt-4 w-full bg-zinc-50 border border-zinc-200 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none resize-none"
                />
                <button onClick={submitRating} disabled={ratingBusy} className="mt-3 bg-black hover:bg-zinc-800 text-orange-500 font-display font-black uppercase tracking-widest px-5 py-3 disabled:opacity-50">
                  {ratingBusy ? "Submitting..." : "Submit rating"}
                </button>
              </section>
            )}

            {active.status === "CANCELLED" && (
              <section className="bg-zinc-900 text-white p-6" data-testid="cancelled-booking-card">
                <p className="font-display font-black text-xl uppercase">Booking cancelled</p>
                <p className="font-mono text-xs text-zinc-400 mt-2">{active.cancel_reason || "This booking is no longer active."}</p>
              </section>
            )}

            <section className="bg-white border border-zinc-200 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Service progress</p>
              <Timeline status={active.status} light rework={Boolean(active.returned_from_qa || active.qa_fail_reasons?.length || active.qa_fail_reason)} />
            </section>

            {active.items && active.items.length > 0 && (
              <section className="bg-white border border-zinc-200 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Parts used</p>
                {active.items.map((it) => (
                  <div key={it.inventory_id} className="flex justify-between py-2 border-b border-zinc-100 last:border-0">
                    <div>
                      <p className="font-bold text-sm">{it.name}</p>
                      <p className="font-mono text-[10px] text-zinc-500">{it.sku} × {it.qty}</p>
                    </div>
                    <p className="font-mono font-bold">₹{it.subtotal}</p>
                  </div>
                ))}
              </section>
            )}

            {data?.history?.length > 1 && (
              <section className="bg-white border border-zinc-200 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Past visits</p>
                <ul className="divide-y divide-zinc-100">
                  {data.history.filter((b) => b.id !== active.id).slice(0, 5).map((b) => (
                    <li key={b.id} className="py-2 flex items-center justify-between text-sm">
                      <div>
                        <p className="font-bold">{b.service_type}</p>
                        <p className="font-mono text-[10px] text-zinc-500">{new Date(b.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-mono text-xs">{b.paid ? `₹${b.bill_amount}` : b.status}</span>
                        {canReopen(b) && (
                          <button onClick={() => reopen(b)} className="block mt-1 font-mono text-[10px] uppercase tracking-widest text-orange-600 hover:text-orange-500">
                            Reopen
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

